import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Modal,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import { exportReportExcel, exportReportPdf } from "../components/Contents/ReportExport";
import ExcelJS from "exceljs";

const REFRESH_INTERVAL_MS = 60000;
const FILE_BASE = "https://mfm.119survey.org/uploads/";
const CSORT_LABEL = { 1: "동원자원", 2: "지원인력" };
const HISTORY_KIND_LABEL = { memo: "메모", order: "지령", status: "상태", support: "지원", handover: "인수인계" };
const IMG_EXTS = /\.(jpg|jpeg|png|gif|webp|bmp)$/i;

// 소방실적 상세: 상황판 엑셀/버튼 서식(바탕색 제외) — 엑셀 아이콘·닫기 버튼 공통
const activityDetailExcelIconSx = {
  width: 30,
  height: 25,
  border: "solid 1px",
  borderColor: "divider",
  borderRadius: 1,
  "&:hover": {
    bgcolor: "action.hover",
    cursor: "pointer",
  },
  zIndex: 10,
};
const activityDetailCloseBtnSx = {
  minHeight: 25,
  border: "solid 1px",
  borderColor: "divider",
  borderRadius: 1,
  "&:hover": {
    bgcolor: "action.hover",
    cursor: "pointer",
  },
  zIndex: 10,
};

// 사진 행에서 실제 이미지 파일명 추출 (cfilename 우선, 없으면 cmsg 폴백)
function extractImageFilename(row) {
  const fn = (row.cfilename || "").trim();
  if (IMG_EXTS.test(fn)) return fn;
  const msg = (row.cmsg || "").trim();
  if (IMG_EXTS.test(msg)) return msg;
  return null;
}

export default function DashboardPage(props) {
  const { apiAddr, onClose } = props;
  const navigate = useNavigate();
  const handleBack = onClose ? () => onClose() : () => navigate("/");
  const dsid = (typeof sessionStorage !== "undefined" && sessionStorage.ondsid) ? (sessionStorage.ondsid || "").trim() : "";
  const dsName = (typeof sessionStorage !== "undefined" && sessionStorage.onname) ? (sessionStorage.onname || "") : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState({ excel: false, pdf: false });
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null); // 클릭한 사진 확대보기
  const [activitySummary, setActivitySummary] = useState(null); // 소방실적 (이력·채팅 기반 동적 분석)
  const [activityDetailModal, setActivityDetailModal] = useState({
    open: false,
    labelKr: "",
    items: [],
    quantityLabels: [],
    loading: false,
    error: null,
  });

  const fetchSummary = useCallback(async () => {
    if (!dsid || !apiAddr) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiAddr}/get/dashboardSummary?dsid=${encodeURIComponent(dsid)}`);
      if (!res.ok) throw new Error("요약 조회 실패");
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || "데이터를 불러오지 못했습니다.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dsid, apiAddr]);

  const fetchPhotos = useCallback(async () => {
    if (!dsid || !apiAddr) return;
    setPhotosLoading(true);
    try {
      const res = await fetch(`${apiAddr}/get/chatphotos?dsid=${encodeURIComponent(dsid)}`);
      const json = await res.json();
      // 이미지 확장자 판별 후 필터링, 실제 파일명 보정
      const filtered = (Array.isArray(json) ? json : [])
        .map((row) => {
          const imgFn = extractImageFilename(row);
          return imgFn ? { ...row, cfilename: imgFn } : null;
        })
        .filter(Boolean);
      setPhotos(filtered);
    } catch (e) {
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  }, [dsid, apiAddr]);

  const fetchActivitySummary = useCallback(async () => {
    if (!dsid || !apiAddr) {
      setActivitySummary(null);
      return;
    }
    try {
      const res = await fetch(`${apiAddr}/get/activitySummary?dsid=${encodeURIComponent(dsid)}`);
      if (!res.ok) {
        setActivitySummary(null);
        return;
      }
      const json = await res.json();
      const raw = json?.items ?? json?.summary ?? (Array.isArray(json) ? json : []);
      setActivitySummary(Array.isArray(raw) ? raw : []);
    } catch (_) {
      setActivitySummary(null);
    }
  }, [dsid, apiAddr]);

  const openActivityDetail = useCallback(
    async (row) => {
      const code = row?.activity_code;
      const label = row?.label_kr ?? row?.label ?? row?.activity_code ?? "";
      const count = row?.total_count ?? row?.count ?? 0;
      if (!code || !apiAddr || !dsid || count <= 0) return;
        setActivityDetailModal({
        open: true,
        labelKr: label,
        items: [],
        quantityLabels: [],
        loading: true,
        error: null,
      });
      try {
        const url = `${apiAddr}/get/activityDetail?dsid=${encodeURIComponent(dsid)}&activity_code=${encodeURIComponent(code)}`;
        const res = await fetch(url);
        let items = [];
        let quantityLabels = [];
        let errorMsg = null;
        if (!res.ok) {
          errorMsg = `서버 오류 (${res.status}). get/activityDetail API가 백엔드에 등록되어 있는지 확인하세요.`;
        } else {
          const json = await res.json().catch(() => null);
          if (Array.isArray(json)) {
            items = json;
          } else if (json && Array.isArray(json.items)) {
            items = json.items;
            quantityLabels = Array.isArray(json.quantityLabels) ? json.quantityLabels : [];
          } else if (json && Array.isArray(json.data)) {
            items = json.data;
          }
        }
        setActivityDetailModal((prev) => ({ ...prev, items, quantityLabels, loading: false, error: errorMsg }));
      } catch (e) {
        setActivityDetailModal((prev) => ({
          ...prev,
          items: [],
          loading: false,
          error: "데이터를 불러오지 못했습니다. (네트워크 또는 서버 확인)",
        }));
      }
    },
    [dsid, apiAddr]
  );

  const closeActivityDetailModal = useCallback(() => {
    setActivityDetailModal({
      open: false,
      labelKr: "",
      items: [],
      quantityLabels: [],
      loading: false,
      error: null,
    });
  }, []);

  const activityDetailExportExcel = useCallback(async () => {
    const items = activityDetailModal.items || [];
    const quantityLabels = activityDetailModal.quantityLabels || [];
    if (items.length === 0) {
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheetName = (activityDetailModal.labelKr || "소방실적상세").replace(/[/\\?*:\[\]]/g, "_").slice(0, 31);
      const worksheet = workbook.addWorksheet(sheetName);
      const headerRow = ["작전지역", "소속", "동원자원명", "출처(채팅, 지령 등)", "해당 텍스트", ...quantityLabels, "입력시간"];
      worksheet.addRow(headerRow);
      items.forEach((item) => {
        const qtyVals = quantityLabels.map((label) => (item.quantities && item.quantities[label] != null ? item.quantities[label] : ""));
        const inputTimeStr = item.inputTime ? new Date(item.inputTime).toLocaleString("ko-KR") : "";
        worksheet.addRow([
          item.region ?? "",
          (item.affiliation != null && String(item.affiliation).trim() !== "") ? item.affiliation : "",
          item.resourceName ?? "",
          item.source ?? "",
          item.text ?? "",
          ...qtyVals,
          inputTimeStr,
        ]);
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `소방실적상세_${sheetName}_${Date.now()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  }, [activityDetailModal.items, activityDetailModal.quantityLabels, activityDetailModal.labelKr]);

  useEffect(() => {
    fetchSummary();
    fetchPhotos();
    fetchActivitySummary();
  }, [fetchSummary, fetchPhotos, fetchActivitySummary]);

  useEffect(() => {
    if (!dsid) return;
    const t = setInterval(() => {
      fetchSummary();
      fetchPhotos();
      fetchActivitySummary();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [dsid, fetchSummary, fetchPhotos, fetchActivitySummary]);

  if (!dsid) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">재난을 선택한 뒤 대시보드를 이용해 주세요.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          홈으로
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">
            홈
          </Button>
          <Typography variant="h6">재난 대응 대시보드</Typography>
          {dsName && (
            <Typography variant="body2" color="text.secondary">
              {dsName}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={async () => {
              setExporting((e) => ({ ...e, excel: true }));
              try {
                await exportReportExcel(apiAddr, dsid, dsName);
              } catch (err) {
                setError(err.message || "엑셀 다운로드 실패");
              } finally {
                setExporting((e) => ({ ...e, excel: false }));
              }
            }}
            disabled={exporting.excel || !data}
            size="small"
            variant="outlined"
          >
            엑셀 다운로드
          </Button>
          <Button
            startIcon={<PictureAsPdfIcon />}
            onClick={async () => {
              setExporting((e) => ({ ...e, pdf: true }));
              try {
                await exportReportPdf(apiAddr, dsid, dsName, data);
              } catch (err) {
                setError(err.message || "PDF 다운로드 실패");
              } finally {
                setExporting((e) => ({ ...e, pdf: false }));
              }
            }}
            disabled={exporting.pdf || !data}
            size="small"
            variant="outlined"
          >
            PDF 다운로드
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={fetchSummary} disabled={loading} size="small">
            새로고침
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <Grid container spacing={2}>
          {/* 동원 요약 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  동원자원 요약
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>구분</TableCell>
                      <TableCell align="right">인원</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow><TableCell>전체</TableCell><TableCell align="right">{data.signboard?.totl ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>집결중</TableCell><TableCell align="right">{data.signboard?.strt ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>대기</TableCell><TableCell align="right">{data.signboard?.wait ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>이동</TableCell><TableCell align="right">{data.signboard?.move ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>활동</TableCell><TableCell align="right">{data.signboard?.actv ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>복귀</TableCell><TableCell align="right">{data.signboard?.rtrn ?? 0}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* 지원인력 요약 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  지원인력 요약
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>구분</TableCell>
                      <TableCell align="right">인원</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow><TableCell>소방</TableCell><TableCell align="right">{data.rdvCount?.fireCount ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>비소방</TableCell><TableCell align="right">{data.rdvCount?.otherCount ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>합계</TableCell><TableCell align="right">{(data.rdvCount?.fireCount ?? 0) + (data.rdvCount?.otherCount ?? 0)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* 채팅·공지 요약 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  채팅·공지 요약
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow><TableCell>공지 건수</TableCell><TableCell align="right">{data.brdCount ?? 0}</TableCell></TableRow>
                    <TableRow><TableCell>채팅 건수</TableCell><TableCell align="right">{data.chatCount ?? 0}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* 이력 요약 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  이력(지령/상태/메모) 요약
                </Typography>
                <Table size="small">
                  <TableBody>
                    {data.historyCounts && Object.keys(data.historyCounts).length > 0 ? (
                      Object.entries(data.historyCounts).map(([kind, cnt]) => (
                        <TableRow key={kind}><TableCell>{HISTORY_KIND_LABEL[kind] ?? kind}</TableCell><TableCell align="right">{cnt}</TableCell></TableRow>
                      ))
                    ) : (
                      <TableRow><TableCell colSpan={2}>이력 없음</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* 소방실적 (이력·채팅 기반 동적 분석) */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  소방실적 (이력·채팅 기반)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>활동 유형</TableCell>
                      <TableCell align="right">건수</TableCell>
                      {activitySummary &&
                        activitySummary.length > 0 &&
                        (() => {
                          const qKeys = [];
                          activitySummary.forEach((r) => {
                            ((r.quantities && Object.keys(r.quantities)) || []).forEach((k) => {
                              if (!qKeys.includes(k)) qKeys.push(k);
                            });
                          });
                          return qKeys.map((k) => (
                            <TableCell key={k} align="right">
                              {k}
                            </TableCell>
                          ));
                        })()}
                      <TableCell align="right">활동시간(분)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activitySummary && activitySummary.length > 0 ? (
                      activitySummary.map((row, i) => {
                        const count = row.total_count ?? row.count ?? 0;
                        const isCountClickable = count > 0;
                        const qKeys = (row.quantities && Object.keys(row.quantities)) || [];
                        const allQKeys =
                          activitySummary?.reduce((acc, r) => {
                            ((r.quantities && Object.keys(r.quantities)) || []).forEach((k) => {
                              if (!acc.includes(k)) acc.push(k);
                            });
                            return acc;
                          }, []) || [];
                        return (
                          <TableRow key={row.activity_code ?? i}>
                            <TableCell>{row.label_kr ?? row.label ?? row.activity_code ?? "-"}</TableCell>
                            <TableCell align="right">
                              {isCountClickable ? (
                                <Button
                                  variant="text"
                                  size="small"
                                  sx={{ minWidth: 0, p: 0.5, textDecoration: "underline", color: "primary.main" }}
                                  onClick={() => openActivityDetail(row)}
                                >
                                  {count}
                                </Button>
                              ) : (
                                count
                              )}
                            </TableCell>
                            {allQKeys.map((k) => (
                              <TableCell key={k} align="right">
                                {row.quantities && row.quantities[k] != null ? row.quantities[k] : "-"}
                              </TableCell>
                            ))}
                            <TableCell align="right">{row.total_duration_minutes ?? row.duration_minutes ?? "-"}</TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ color: "text.secondary", py: 2 }}>
                          실적 데이터가 없거나 분석 중입니다. (백엔드 get/activitySummary 연동 후 표시됩니다)
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* 소방실적 상세 모달: 작전지역 | 동원자원명 | 출처 | 해당 텍스트 | 입력시간 */}
      <Modal open={activityDetailModal.open} onClose={closeActivityDetailModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 1100,
            maxHeight: "85vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" fontWeight="bold">
              소방실적 상세 — {activityDetailModal.labelKr || "활동 유형"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="엑셀 내려받기" placement="left" arrow>
                <IconButton
                  size="small"
                  onClick={activityDetailExportExcel}
                  disabled={!activityDetailModal.items || activityDetailModal.items.length === 0}
                  sx={activityDetailExcelIconSx}
                  aria-label="엑셀 내려받기"
                >
                  <DownloadIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={closeActivityDetailModal}
                sx={activityDetailCloseBtnSx}
                variant="outlined"
              >
                닫기
              </Button>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {activityDetailModal.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : activityDetailModal.error ? (
              <Typography variant="body2" color="error" sx={{ textAlign: "center", py: 3 }}>
                {activityDetailModal.error}
              </Typography>
            ) : !activityDetailModal.items || activityDetailModal.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                해당 유형의 상세 실적이 없습니다.
              </Typography>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: 60 }}>작전지역</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: 60 }}>소속</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: 80 }}>동원자원명</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: 90 }}>출처(채팅, 지령 등)</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>해당 텍스트</TableCell>
                    {(activityDetailModal.quantityLabels || []).map((label) => {
                      const sum = (activityDetailModal.items || []).reduce(
                        (acc, item) => acc + (typeof (item.quantities && item.quantities[label]) === "number" ? item.quantities[label] : parseFloat(item.quantities && item.quantities[label]) || 0),
                        0
                      );
                      return (
                        <TableCell key={label} sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: 56 }} align="right">
                          {label}({sum})
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: 130 }}>입력시간</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityDetailModal.items.map((item, idx) => {
                    const aff = (item.affiliation != null && String(item.affiliation).trim() !== "")
                      ? item.affiliation
                      : (() => {
                          const rn = (item.resourceName || "").toString().trim();
                          if (!rn) return "-";
                          const first = rn.split(/\s+/)[0] || "";
                          return (first && String(first).trim() !== "") ? first : "-";
                        })();
                    return (
                    <TableRow key={idx}>
                      <TableCell sx={{ whiteSpace: "nowrap", minWidth: 60 }}>{item.region ?? "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", minWidth: 60 }}>{aff}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", minWidth: 80 }}>{item.resourceName ?? "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", minWidth: 90 }}>{item.source ?? "-"}</TableCell>
                      <TableCell sx={{ maxWidth: 280, wordBreak: "break-word", minWidth: 100 }}>{item.text ?? "-"}</TableCell>
                      {(activityDetailModal.quantityLabels || []).map((label) => (
                        <TableCell key={label} align="right" sx={{ whiteSpace: "nowrap", minWidth: 56 }}>
                          {item.quantities && item.quantities[label] != null ? item.quantities[label] : "-"}
                        </TableCell>
                      ))}
                      <TableCell sx={{ whiteSpace: "nowrap", minWidth: 130 }}>{item.inputTime ? new Date(item.inputTime).toLocaleString("ko-KR") : "-"}</TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
      </Modal>

      {/* 사진 갤러리 */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            📷 대화방 사진 ({photos.length}장)
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip label="동원자원" size="small" sx={{ bgcolor: "#ffcc80", fontSize: "0.75rem" }} />
            <Chip label="지원인력" size="small" sx={{ bgcolor: "#66bb6a", color: "white", fontSize: "0.75rem" }} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchPhotos} disabled={photosLoading}>
              새로고침
            </Button>
          </Box>
        </Box>
        <Card>
          <CardContent sx={{ p: "12px !important" }}>
            {photosLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : photos.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                등록된 사진이 없습니다.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                  gap: "6px",
                  maxHeight: 420,
                  overflowY: "auto",
                }}
              >
                {photos.map((p) => {
                  const url = `${FILE_BASE}${p.cfilename}`;
                  const label = CSORT_LABEL[p.csort] || "";
                  const dt = p.cipdt ? new Date(p.cipdt).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
                  return (
                    <Box
                      key={p.snum}
                      onClick={() => setLightboxPhoto(p)}
                      sx={{
                        position: "relative",
                        paddingTop: "100%", // 정사각형 비율
                        cursor: "pointer",
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid #e0e0e0",
                        "&:hover .photo-overlay": { opacity: 1 },
                        "&:hover img": { transform: "scale(1.06)" },
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt={p.cfilename}
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.2s ease",
                        }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      {/* 호버 오버레이 */}
                      <Box
                        className="photo-overlay"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(0,0,0,0.45)",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end",
                          p: "4px 6px",
                        }}
                      >
                        <Typography sx={{ color: "white", fontSize: "0.65rem", lineHeight: 1.3 }}>
                          {p.cname}
                        </Typography>
                        <Typography sx={{ color: "#ccc", fontSize: "0.6rem" }}>{dt}</Typography>
                      </Box>
                      {/* 출처 뱃지 */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 3,
                          left: 3,
                          bgcolor: Number(p.csort) === 2 ? "rgba(46,125,50,0.95)" : "rgba(255,152,0,0.9)",
                          color: "white",
                          fontSize: "0.55rem",
                          px: "4px",
                          py: "1px",
                          borderRadius: "3px",
                          lineHeight: 1.5,
                        }}
                      >
                        {label}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* 사진 확대 라이트박스 */}
      <Modal open={!!lightboxPhoto} onClose={() => setLightboxPhoto(null)}>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.88)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
          }}
          onClick={() => setLightboxPhoto(null)}
        >
          {lightboxPhoto && (
            <>
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 16,
                  color: "white",
                  cursor: "pointer",
                  fontSize: "2rem",
                  lineHeight: 1,
                  zIndex: 1,
                }}
                onClick={() => setLightboxPhoto(null)}
              >
                <CloseIcon sx={{ fontSize: "2rem" }} />
              </Box>
              <Box sx={{ mb: 1.5, textAlign: "center" }}>
                <Chip
                  label={CSORT_LABEL[lightboxPhoto.csort] || ""}
                  size="small"
                  sx={{
                    bgcolor: Number(lightboxPhoto.csort) === 2 ? "#2e7d32" : "#ff9800",
                    color: "white",
                    mr: 1,
                  }}
                />
                <Typography component="span" sx={{ color: "#ddd", fontSize: "0.85rem" }}>
                  {lightboxPhoto.cname} &nbsp;·&nbsp;{" "}
                  {lightboxPhoto.cipdt ? new Date(lightboxPhoto.cipdt).toLocaleString("ko-KR") : ""}
                </Typography>
              </Box>
              <Box
                component="img"
                src={`${FILE_BASE}${lightboxPhoto.cfilename}`}
                alt={lightboxPhoto.cfilename}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  maxWidth: "90vw",
                  maxHeight: "80vh",
                  objectFit: "contain",
                  borderRadius: 1,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
                }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1.5, color: "white", borderColor: "rgba(255,255,255,0.4)" }}
                onClick={(e) => { e.stopPropagation(); window.open(`${FILE_BASE}${lightboxPhoto.cfilename}`, "_blank"); }}
              >
                원본 열기
              </Button>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
}
